import { useId, useState } from 'react';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import debounce from 'lodash.debounce';
import FormGroup from './FormGroup';
import AsyncSelect from 'react-select/async';
import { getSharedStyles } from '../../utils/select';
import type { AgentGardenAgentAPI, AgentMode } from '../../types/agent';
import { getAgent, getAllAgents } from '../../sources/agent';

interface Props {
    accountId: string | null;
    label: string;
    value: string | null;
    onChange: (agent: AgentGardenAgentAPI | null) => void;
    isRequired?: boolean;
    showValidation?: boolean;
    validationMessage?: string;
    className?: string;
    agentMode?: AgentMode;
}

const AgentDropdown = ({
    accountId,
    label,
    value,
    onChange,
    isRequired = false,
    showValidation = false,
    validationMessage = '',
    className = '',
    agentMode,
}: Props) => {
    const dispatchNotification = useNotificationDispatch();

    const [loadingAgents, setLoadingAgents] = useState(false);
    const [agent, setAgent] = useState<AgentGardenAgentAPI | null>(null);

    const agentInputId = useId();

    const loadAgentOptions = debounce(
        async (search: string, callback: (options: AgentGardenAgentAPI[]) => void) => {
            try {
                if (accountId) {
                    setLoadingAgents(true);

                    let agents = await getAllAgents(accountId, search, agentMode || null);
                    let agent = agents.find((agent) => agent.id === value) || null;

                    callback(agents);

                    if (!agent && value) {
                        agent = await getAgent(accountId, value);
                        agents = [agent, ...agents];
                    }

                    setAgent(agent);
                    callback(agents);
                }
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setLoadingAgents(false);
            }
        },
        500,
    ) as (inputValue: string, callback: (options: AgentGardenAgentAPI[]) => void) => void;

    return (
        <FormGroup
            label={label}
            isRequired={isRequired}
            isValid={!!agent}
            showValidation={showValidation}
            validationMessage={validationMessage}
            htmlFor={agentInputId}
            className={className}
        >
            <AsyncSelect
                key={accountId}
                isDisabled={!accountId}
                inputId={agentInputId}
                isMulti={false}
                isSearchable={true}
                className="margin-bottom-small"
                loadOptions={loadAgentOptions}
                onChange={(newValue) => {
                    setAgent(newValue);
                    onChange(newValue);
                }}
                value={agent}
                isLoading={loadingAgents}
                getOptionLabel={(agent) => agent.name}
                getOptionValue={(agent) => agent.id}
                defaultOptions
                styles={getSharedStyles<AgentGardenAgentAPI>()}
            />
        </FormGroup>
    );
};

export default AgentDropdown;
